import Button from '@rio-cloud/rio-uikit/Button';
import classNames from 'classnames';
import { mapValues } from 'es-toolkit/object';
import { FunctionComponent, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { runInBackgroundCallback } from '../../../../../configuration/setup/backgroundActions';
import { useAppDispatch } from '../../../../../configuration/setup/hooks';
import { usePutArrivalCompletionMutation } from '../../../api/compoundManagementApi';
import { handleQueryError } from '../../../notifications/ErrorNotification';
import { showSuccessNotification } from '../../../notifications/SuccessNotification';
import { compoundManagementSlice } from '../../../reducers/compoundManagementSlice';
import { TransportInformation } from '../../compoundManagement.types';
import { CompletedStatusAtInput } from './CompletedStatusAtInput';
import { DetailsFormHighlight } from './DetailsFormHighlight';
import { MeansOfTransportTypeInput } from './formInputs/MeansOfTransportTypeInput';
import { TransportInformationInputs } from './formInputs/TransportInformationInputs';
import { getStatusDateValidationMessageId } from './validateStatusDate';

type ArrivalCompletedFormProps = {
    timeZone: string;
    compoundVehicleId: string;
    disabled: boolean;
    arrivalCompletedAt: Date | undefined;
    transportInformation: TransportInformation;
};

type ArrivalCompletedFormState = {
    arrivalCompletedAt: Date | string | undefined;
    transportInformation: TransportInformation;
};

export const ArrivalCompletedForm: FunctionComponent<ArrivalCompletedFormProps> = ({
    timeZone,
    compoundVehicleId,
    disabled,
    arrivalCompletedAt: previousArrivalCompletedAt,
    transportInformation: previousTransportInformation,
}) => {
    const {
        formState,
        isLoading,
        validationErrorMessageIds,
        setArrivalCompletedAt,
        setTransportInformation,
        handleConfirmArrival,
    } = useArrivalCompletedForm(compoundVehicleId, {
        arrivalCompletedAt: previousArrivalCompletedAt,
        transportInformation: previousTransportInformation,
    });
    const isUpdate = previousArrivalCompletedAt !== undefined;

    return (
        <DetailsFormHighlight disabled={disabled}>
            <CompletedStatusAtInput
                labelId="outboundOrderBook.compoundManagement.table.heading.arrivalDate"
                timeZone={timeZone}
                disabled={disabled}
                statusDate={formState.arrivalCompletedAt}
                onChangeStatusDate={setArrivalCompletedAt}
                validationErrorId={validationErrorMessageIds.arrivalCompletedAt}
            />
            <MeansOfTransportTypeInput disabled value={formState.transportInformation.meansOfTransportType} />
            <TransportInformationInputs
                transportInformation={formState.transportInformation}
                setTransportInformation={setTransportInformation}
                disabled={disabled}
            />
            <hr className="width-100pct grid-column-span-2" />
            <div className="grid-column-span-2 display-flex gap-25 justify-content-end">
                {isUpdate ? (
                    <UpdateArrivalButton disabled={disabled} isLoading={isLoading} onClick={handleConfirmArrival} />
                ) : (
                    <ConfirmArrivalButton disabled={disabled} isLoading={isLoading} onClick={handleConfirmArrival} />
                )}
            </div>
        </DetailsFormHighlight>
    );
};

const useArrivalCompletedForm = (compoundVehicleId: string, previousState: ArrivalCompletedFormState) => {
    const dispatch = useAppDispatch();
    const [completeArrival, completeArrivalRequest] = usePutArrivalCompletionMutation();

    const [submitted, setSubmitted] = useState(false);
    const [formState, setFormState] = useState<ArrivalCompletedFormState>(previousState);

    const validationErrorMessageIds = {
        arrivalCompletedAt: getStatusDateValidationMessageId(formState.arrivalCompletedAt),
    };

    const setArrivalCompletedAt = (arrivalCompletedAt: Date | string | undefined) =>
        setFormState((previous) => ({ ...previous, arrivalCompletedAt }));

    const setTransportInformation = (change: Partial<TransportInformation>) =>
        setFormState((previous) => ({
            ...previous,
            transportInformation: { ...previous.transportInformation, ...change },
        }));

    const handleConfirmArrival = runInBackgroundCallback(async () => {
        setSubmitted(true);

        const isValid = Object.values(validationErrorMessageIds).every((messageId) => messageId === undefined);
        if (isValid) {
            try {
                await completeArrival({
                    compoundVehicleIds: [compoundVehicleId],
                    statusAt: formState.arrivalCompletedAt! as Date,
                    transportInformation: formState.transportInformation,
                }).unwrap();

                const isUpdate = previousState.arrivalCompletedAt !== undefined;
                showSuccessNotification(
                    isUpdate
                        ? 'outboundOrderBook.compoundManagement.notification.inventoryVehicle.updateArrival.success'
                        : 'outboundOrderBook.compoundManagement.notification.incomingVehicle.confirmArrival.success',
                );
                dispatch(compoundManagementSlice.actions.deselectCompoundVehicle());
            } catch (error: unknown) {
                handleQueryError(error);
            }
        }
    });

    return {
        isLoading: completeArrivalRequest.isLoading,
        formState,
        validationErrorMessageIds: mapValues(validationErrorMessageIds, (messageId) =>
            submitted ? messageId : undefined,
        ),
        setArrivalCompletedAt,
        setTransportInformation,
        handleConfirmArrival,
    };
};

const ConfirmArrivalButton: FunctionComponent<{ disabled: boolean; isLoading: boolean; onClick: () => void }> = ({
    disabled,
    isLoading,
    onClick,
}) => (
    <Button
        className={classNames({ 'btn-loading': isLoading })}
        disabled={disabled || isLoading}
        iconName="rioglyph-load"
        bsStyle={Button.PRIMARY}
        onClick={onClick}
    >
        <FormattedMessage id="outboundPortal.compoundManagement.button.confirmArrival" />
    </Button>
);

const UpdateArrivalButton: FunctionComponent<{ disabled: boolean; isLoading: boolean; onClick: () => void }> = ({
    disabled,
    isLoading,
    onClick,
}) => (
    <Button
        className={classNames({ 'btn-loading': isLoading })}
        disabled={disabled || isLoading}
        iconName="rioglyph-refresh"
        bsStyle={Button.DEFAULT}
        onClick={onClick}
    >
        <FormattedMessage id="outboundPortal.compoundManagement.button.updateArrival" />
    </Button>
);
